import { useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Edit as EditIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Quote } from '../types/api';
import { QuotesRoute, EditQuoteRoute } from '../routes';
import { deleteQuote } from '../services/quotes.service';
import GlobalMessageContext from '../context/globalMessage.context';
import { QuoteSummary } from '../components/quote-summary.component';
import { dataTables } from '../styles';

export default function ViewQuoteView() {
  const location = useLocation();
  const navigate = useNavigate();
  const uxMessage = useContext(GlobalMessageContext);
  const [quote, setQuote] = useState<Quote | null>(location.state?.quote || null);

  if (!quote) {
    return (
      <>
        <Typography variant="h1">Quote not found</Typography>
        <Box sx={{ m: 1 }}>
          <Button variant="outlined" onClick={() => navigate(QuotesRoute)}>
            Back to Quotes
          </Button>
        </Box>
      </>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value / 100);
  };

  const handleEdit = () => {
    navigate(EditQuoteRoute, { state: { mode: 'edit', quote } });
  };

  const handleDelete = async () => {
    if (quote.status !== 'DRAFT') {
      uxMessage.setMessage({
        message: 'Only DRAFT quotes can be deleted',
        severity: 'warning',
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete quote ${quote.number}?`)) {
      return;
    }

    try {
      await deleteQuote(quote.guid!);
      uxMessage.setMessage({
        message: `Quote ${quote.number} deleted successfully`,
        severity: 'success',
      });
      navigate(QuotesRoute);
    } catch (error: any) {
      console.error('Error deleting quote:', error);
      uxMessage.setMessage({
        message: `Failed to delete quote: ${error.response?.data?.message || error.message}`,
        severity: 'error',
      });
    }
  };

  const handleConvertToOrder = () => {
    // This will be implemented in Task 5
    uxMessage.setMessage({
      message: 'Convert to Order functionality will be implemented in Task 5',
      severity: 'info',
    });
  };

  const getStatusColor = (status: string): 'default' | 'info' | 'success' | 'warning' => {
    const colors: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
      DRAFT: 'default',
      SENT: 'info',
      ACCEPTED: 'success',
      DECLINED: 'warning',
    };
    return colors[status] || 'default';
  };

  const getSyncStatusColor = (syncStatus: string): 'success' | 'warning' | 'error' => {
    const colors: Record<string, 'success' | 'warning' | 'error'> = {
      SYNCED: 'success',
      PENDING: 'warning',
      FAILED: 'error',
    };
    return colors[syncStatus] || 'warning';
  };

  // Define DataGrid columns for line items
  const columns: GridColDef[] = [
    {
      field: 'sku',
      headerName: 'SKU',
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 200,
      flex: 2,
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      minWidth: 100,
      flex: 1,
      align: 'right',
    },
    {
      field: 'unitPrice',
      headerName: 'Unit Price',
      minWidth: 120,
      flex: 1,
      align: 'right',
      renderCell: (params) => formatCurrency(params.row.unitPrice),
    },
    {
      field: 'lineTotal',
      headerName: 'Line Total',
      minWidth: 120,
      flex: 1,
      align: 'right',
      renderCell: (params) => formatCurrency(params.row.lineTotal),
    },
  ];

  return (
    <>
      <Typography variant="h1">Quote {quote.number}</Typography>

      <Box sx={{ m: 1 }}>
        <Grid container spacing={2}>
          {/* Customer info and status chips */}
          <Grid size={6}>
            <Typography variant="body1">
              <strong>Customer:</strong> {quote.customerName}
            </Typography>
            {quote.customerEmail && (
              <Typography variant="body2" color="text.secondary">
                {quote.customerEmail}
              </Typography>
            )}
            {quote.customerPhone && (
              <Typography variant="body2" color="text.secondary">
                {quote.customerPhone}
              </Typography>
            )}
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Chip label={quote.status} color={getStatusColor(quote.status)} size="small" />
              <Chip label={quote.syncStatus} color={getSyncStatusColor(quote.syncStatus)} size="small" />
              {quote.archived && <Chip label="ARCHIVED" color="default" size="small" />}
            </Box>
          </Grid>

          {/* Action buttons */}
          <Grid size={6}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {quote.status === 'DRAFT' && (
                <>
                  <Tooltip title="Edit Quote">
                    <Button
                      variant="contained"
                      color="inherit"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleEdit}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                  <Tooltip title="Delete Quote">
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={handleDelete}
                    >
                      Delete
                    </Button>
                  </Tooltip>
                </>
              )}
              {(quote.status === 'SENT' || quote.status === 'ACCEPTED') && !quote.shopifyOrderId && (
                <Tooltip title="Convert to Shopify Order">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleConvertToOrder}
                  >
                    Convert to Order
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Back to Quotes List">
                <Button
                  variant="contained"
                  color="inherit"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(QuotesRoute)}
                >
                  Back
                </Button>
              </Tooltip>
            </Stack>
          </Grid>

          {/* Summary table */}
          <Grid size={4}>
            <QuoteSummary
              quote={quote}
              subTotal={quote.subTotal}
              taxAmount={quote.taxAmount}
              total={quote.total}
            />
          </Grid>

          {/* Line Items */}
          <Grid size={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              Line Items
            </Typography>
            <DataGrid
              rows={quote.lineItems}
              columns={columns}
              getRowId={(row) => row.guid || row.id}
              autoHeight
              density="compact"
              rowHeight={60}
              disableRowSelectionOnClick
              hideFooter
              sx={dataTables}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
